import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs';

import { ComponentBase } from '../component-base';
import { BuildFloorService } from './build-floor.service';
import { StageService } from './stage.service';
import { SceneUtilsService } from './scene-utils.service';
import { MoveCameraService } from '../Component(2D and 3D)/2D/move-camera.service';
import { HelperLinesService } from './helper-lines.service';
import { CameraService } from './camera.service';
import { DeckHeightService } from './deck-height.service';
import { SizeHelperService } from './size-helper.service';
import { RailingGenerationService } from './railing/services/railing-generation.service';
import { Select3dService } from './select-3d.service';
import { ConstantsService } from './constants.service';
import { Position } from '../classes/position';
import { Rotation } from '../classes/rotation';
import { EventLogService, Actions } from './eventLog';
import { AutoScaleSprite } from '../custom/AutoScaleSprite';
import { FloorHull } from '../custom/FloorHull';
import { LoadingSpinnerService } from './loading-spinner.service';
import { ImageUtilsService } from './image-utils.service';
import { AwningSaveDataService } from './awning/awning-save-data.service';
import { isSecondaryTouchPointer } from '../utils/pointer-input.utils';
import { Mobile3dResizeOrbitLockService } from './mobile-3d-resize-orbit-lock.service';

declare const THREE: any;

interface ISpriteData {
  firstPoint: any;
  secondPoint: any;
  angles: number[];
  vectors: any[];
  number: number;
  normal: any;
}

@Injectable()
export class DragFloorEdge extends ComponentBase {
  private _newPosition = new THREE.Vector3();
  private _oldPosition = new THREE.Vector3();
  private _points;
  private _edges;
  private _raycaster = new THREE.Raycaster();
  private _pointsExeptDragged = [];
  private _sprites = [];
  private _loader = new THREE.TextureLoader();
  private _wallDirectionX: string;
  private _wallDirectionY: string;
  private _currentFloor: any;
  private _selectTierSubscription: Subscription | null = null;
  private _resizeTierAction: Actions.ResizeTierAction;
  private _floorHull: FloorHull | null = null;
  private _newPoints;
  private _neighbourTiers = [];
  private _currentSpriteData: ISpriteData | null = null;
  private _dragging = false;
  private _resizeEdgePointerId: number | null = null;
  private _lastTouchOffsetX = 0;
  private _lastTouchOffsetY = 0;
  /** Синхронизация с Mobile3dResizeOrbitLockService: стрелки видны и touch+3D. */
  private _mobileFloorResizeCameraLockActive = false;
  tierResized = new Subject<any>();

  constructor(
    private floorBuilder: BuildFloorService,
    private stage: StageService,
    private sceneUtils: SceneUtilsService,
    private moveCamera: MoveCameraService,
    private helperLines: HelperLinesService,
    private camera: CameraService,
    private deckHeight: DeckHeightService,
    private sizeHelper: SizeHelperService,
    private railingGeneration: RailingGenerationService,
    private select3dService: Select3dService,
    private constants: ConstantsService,
    private eventLogSrvc: EventLogService,
    private loadingSpinnerService: LoadingSpinnerService,
    private imageUtils: ImageUtilsService,
    private awningSaveDataService: AwningSaveDataService,
    private mobile3dResizeOrbitLock: Mobile3dResizeOrbitLockService
  ) {
    super();
    this.disable();
  }

  private _setOffsetXYFromTouchEvent(e: any) {
    if (!e || !e.target || !e.targetTouches || e.targetTouches.length === 0) return;
    const target = e.target as HTMLElement;
    const touches = e.targetTouches;

    const rect = target.getBoundingClientRect();
    const firstTouch = touches[0];
    e.offsetX = firstTouch.pageX - rect.left;
    e.offsetY = firstTouch.pageY - rect.top;
  }

  touchStart(e: any) {
    this._setOffsetXYFromTouchEvent(e);
    if (e && typeof e.offsetX === 'number') this._lastTouchOffsetX = e.offsetX;
    if (e && typeof e.offsetY === 'number') this._lastTouchOffsetY = e.offsetY;
    return this.mouseDown(e as MouseEvent);
  }

  touchMove(e: any) {
    if (e && e.preventDefault) e.preventDefault();
    this._setOffsetXYFromTouchEvent(e);
    if (e && typeof e.offsetX === 'number') this._lastTouchOffsetX = e.offsetX;
    if (e && typeof e.offsetY === 'number') this._lastTouchOffsetY = e.offsetY;
    return this.mouseMove(e as MouseEvent);
  }

  touchEnd(e: any) {
    // TouchEvent on touchend has no targetTouches; reuse last known offsets.
    e.offsetX = this._lastTouchOffsetX;
    e.offsetY = this._lastTouchOffsetY;
    return this.mouseUp();
  }

  private _calculateSpriteData(points): ISpriteData[] {
    const result = [];
    const up = new THREE.Vector3(0, 1, 0);

    for (let i = 0; i < points.length; i++) {
      const firstPoint = points[i];
      const secondPoint = points[(i + 1) % points.length];

      if (!firstPoint.superstructureType) {
        const diff = new THREE.Vector3(
          secondPoint.x - firstPoint.x,
          0,
          secondPoint.y - firstPoint.y
        );
        const angle = -Math.atan2(diff.z, diff.x);
        const ppPoint = points[i - 1] || points[points.length - 1];
        const nnPoint = points[(i + 2) % points.length];
        const pPoint = firstPoint;
        const nPoint = secondPoint;
        const vector1 = new THREE.Vector3(pPoint.x - ppPoint.x, 0, pPoint.y - ppPoint.y);
        const vector2 = new THREE.Vector3(nPoint.x - nnPoint.x, 0, nPoint.y - nnPoint.y);

        let firstAngle = -Math.atan2(vector1.z, vector1.x);
        let secondAngle = -Math.atan2(vector2.z, vector2.x) - Math.PI;
        firstAngle = Math.abs(angle - firstAngle);
        secondAngle -= angle;
        secondAngle = Math.abs(secondAngle);

        result.push({
          firstPoint,
          secondPoint,
          angles: [firstAngle, secondAngle],
          vectors: [vector1, vector2, diff],
          number: i,
          normal: diff
            .clone()
            .applyAxisAngle(up, Math.PI / 2)
            .normalize()
        });
      }
    }

    return result;
  }

  private _drawSprites(floor) {
    const points = floor.userData.points;
    const floorHeight = floor.getWorldPosition(new THREE.Vector3()).y;
    const spritesData = this._calculateSpriteData(points);
    spritesData.forEach(spriteData => {
      const sprite = this._createSprite(spriteData);
      sprite.userData.data = spriteData;
      sprite.position.y = floorHeight;
      this._sprites.push(sprite);
      this.stage.add(sprite);
    });
    const pts = points.map(p => new THREE.Vector3(p.x, 0, p.y));
    this._floorHull = new FloorHull(pts);
    this._floorHull.position.y = floorHeight;
    this.stage.add(this._floorHull);
    this._syncMobile3dFloorResizeCameraLock();
  }

  private _syncMobile3dFloorResizeCameraLock() {
    const want =
      this._sprites.length > 0 &&
      this.camera.isIn3D() &&
      typeof navigator !== 'undefined' &&
      navigator.maxTouchPoints > 0;
    if (want === this._mobileFloorResizeCameraLockActive) {
      return;
    }
    this._mobileFloorResizeCameraLockActive = want;
    if (want) {
      this.mobile3dResizeOrbitLock.begin();
    } else {
      this.mobile3dResizeOrbitLock.end();
    }
  }

  private _updateSprites() {
    const middlePoints = this._floorHull.middlePoints();
    this._sprites.forEach(sprite => {
      const data = sprite.userData.data as ISpriteData;
      const point = middlePoints[data.number];
      sprite.position.x = point.x;
      sprite.position.z = point.z;
    });
  }

  private _createSprite(spriteData: ISpriteData) {
    const spriteMap = this._loader.load('../../assets/images/sprites/size_grip.png');
    const fp = new THREE.Vector3(spriteData.firstPoint.x, 0, spriteData.firstPoint.y);
    const sp = new THREE.Vector3(spriteData.secondPoint.x, 0, spriteData.secondPoint.y);
    const fpOnScreen = this.sceneUtils.worldToScreen(fp.clone());
    const spOnScreen = this.sceneUtils.worldToScreen(sp.clone());
    const diff = new THREE.Vector3(spOnScreen.x - fpOnScreen.x, 0, fpOnScreen.y - spOnScreen.y);
    const angle = Math.atan2(diff.z, diff.x);

    const spriteMaterial = new THREE.SpriteMaterial({
      map: spriteMap,
      color: 0xffffff,
      depthTest: false,
      depthWrite: false,
      rotation: angle
    });

    const delta = sp.clone().sub(fp);
    const length = delta.length();
    const direction = delta.normalize();
    const middle = fp.add(direction.multiplyScalar(length / 2));

    const sprite = new AutoScaleSprite(30, 30, spriteMaterial);

    sprite.position.set(middle.x, 0, middle.z);

    return sprite;
  }

  private _rebuildFloor(): Promise<any> {
    return this.floorBuilder.buildFloorAsync(
      {
        points: this._newPoints
      },
      this._currentFloor.id
    );
  }

  private _rebuildNeighbourTiers(rebuildedFloor): Promise<any> {
    const promises = this._neighbourTiers.map(tier =>
      this.floorBuilder.buildFloorAsync({}, tier.id)
    );
    return Promise.all(promises).then(_ => rebuildedFloor);
  }

  private async _applyOffset(rebuildedFloor): Promise<any> {
    const { number: edgeIndex } = this._currentSpriteData;
    const { points, customId: floorId } = rebuildedFloor.userData;
    const contactPointsIndexes = this.getContactPointsIndex(edgeIndex, points.length);
    const contactTiers = this.checkContactTiets(
      contactPointsIndexes.first,
      contactPointsIndexes.second,
      edgeIndex
    );
    let previousOffset = 0;
    let alreadyApplied = false;
    let floor = rebuildedFloor;

    for (let i = 0; i < contactTiers.length; i++) {
      const tier = contactTiers[i];
      let fp, sp, index;
      index = tier.tier.userData.points.findIndex(
        el => el.parent || (el.childCustomId && floorId === el.childCustomId)
      );
      if (tier.edgeOrder === 'first') {
        fp = tier.tier.userData.edges[index].secondPoint;
        sp = tier.tier.userData.edges[index].firstPoint;
      } else if (tier.edgeOrder === 'second') {
        fp = tier.tier.userData.edges[index].firstPoint;
        sp = tier.tier.userData.edges[index].secondPoint;
      }
      const offset = this._checkSpaces(points[tier.pointIndex], fp, sp, tier.edgeOrder, tier.value);
      if (
        alreadyApplied &&
        Math.round(offset) !== 0 &&
        previousOffset &&
        Math.round(Math.abs(offset) + Math.abs(offset)) !== 30
      ) {
        const newOffset = previousOffset > 0 ? previousOffset + 30 : -previousOffset - 30;
        floor = await this._applyOffsetToEdge(
          newOffset,
          edgeIndex,
          contactTiers[0].tier.edgeOrder,
          floor
        );
        alreadyApplied = true;
      } else {
        floor = await this._applyOffsetToEdge(offset, edgeIndex, tier.edgeOrder, floor);
        alreadyApplied = true;
      }
      previousOffset = offset;
    }

    return floor;
  }

  private async _applyNewSize(): Promise<void> {
    this._unSubscribeFromSelect();
    await this._rebuildFloor()
      .then(this._rebuildNeighbourTiers.bind(this))
      .then(this._applyOffset.bind(this))
      .then(rebuildedFloor => {
        this.railingGeneration.buildRailing(this.railingGeneration.IsVisible());
        if (this.camera.isIn2D()) {
          this.moveCamera.enable();
        }
        this._pointsExeptDragged = [];
        this.stage.removeMany(this.helperLines.getLines());
        this._points = [];
        this.eventLogSrvc.dispatch(this._resizeTierAction);
        this.tierResized.next(rebuildedFloor);
        this._currentSpriteData = null;
        this._subscribeForSelect();
        this.select3dService.setSelectedTier(rebuildedFloor);
        if (!this.imageUtils.isShowDimensionsValue) {
          this.imageUtils.hideSprites();
        }
      });
  }

  private _getTier(id) {
    return this.stage.getCompoundFloor().children.find(tier => tier.userData.customId === id);
  }

  private _checkSpaces(edgePoint, contactPoint, contactEdgePoint, edgeOrder, value) {
    let selectedPoint = new THREE.Vector3(edgePoint.x, 0, edgePoint.y);
    let fp = new THREE.Vector3(contactPoint.x, 0, contactPoint.y);
    let sp = new THREE.Vector3(contactEdgePoint.x, 0, contactEdgePoint.y);
    let distanceToFp = selectedPoint.distanceTo(fp);
    let distanceToSp = selectedPoint.distanceTo(sp);
    let edgeLength = fp.distanceTo(sp);
    this.constants.tierTypeSettingsMap[value].distanceToTier;
    let offsetFp = Math.sqrt(
      distanceToFp * distanceToFp -
        this.constants.tierTypeSettingsMap[value].distanceToTier *
          this.constants.tierTypeSettingsMap[value].distanceToTier
    );
    let offsetSp = Math.sqrt(
      distanceToSp * distanceToSp -
        this.constants.tierTypeSettingsMap[value].distanceToTier *
          this.constants.tierTypeSettingsMap[value].distanceToTier
    );
    let offset = 0;
    if (offsetSp > edgeLength) {
      if (offsetFp < 15) {
        offset = -offsetFp;
      } else if (offsetFp < 30) {
        offset = 30 - offsetFp;
      }
    } else {
      if (offsetFp < 15) {
        offset = offsetFp;
      } else if (offsetFp < 30) {
        offset = offsetFp - 30;
      }
    }
    return offset;
  }

  private _checkWallDirection(firstPoint, secondPoint) {
    if (firstPoint.x < secondPoint.x && this._wallDirectionX === 'positive') {
      return true;
    } else if (firstPoint.x > secondPoint.x && this._wallDirectionX === 'negative') {
      return true;
    } else if (firstPoint.x === secondPoint.x && this._wallDirectionX === 'neutral') {
      if (firstPoint.z < secondPoint.z && this._wallDirectionY === 'negative') {
        return true;
      } else if (firstPoint.z > secondPoint.z && this._wallDirectionY === 'positive') {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  private _setWallDirection(points) {
    if (points[0].x < points[1].x) {
      this._wallDirectionX = 'positive';
    } else if (points[0].x > points[1].x) {
      this._wallDirectionX = 'negative';
    } else {
      this._wallDirectionX = 'neutral';
    }
    if (points[0].y > points[1].y) {
      this._wallDirectionY = 'positive';
    } else if (points[0].y < points[1].y) {
      this._wallDirectionY = 'negative';
    } else {
      this._wallDirectionY = 'neutral';
    }
  }

  private _subscribeForSelect() {
    if (!this._selectTierSubscription) {
      this._selectTierSubscription = this.select3dService.selectedTierSubject.subscribe(
        selectedTier => {
          this.removeSprites(true);
          this._drawSprites(selectedTier);
          this._currentFloor = selectedTier;
        }
      );
    }
  }

  private _unSubscribeFromSelect() {
    if (this._selectTierSubscription) {
      this._selectTierSubscription.unsubscribe();
      this._selectTierSubscription = null;
    }
  }

  private _gatherWindowsDoorsData(floor: any, wallId: number): Array<any> {
    let windowsDoors = this.stage.getWindowsDoorsByFloorAndWallId(floor, wallId);
    let result = [];
    if (windowsDoors.length > 0) {
      windowsDoors.map(wd => {
        const rot = new THREE.Euler().setFromQuaternion(wd.getWorldQuaternion(), 'XYZ');
        result.push({
          id: wd.userData.entity.id,
          position: new Position(
            wd.getWorldPosition(new THREE.Vector3()).x,
            wd.getWorldPosition(new THREE.Vector3()).y,
            wd.getWorldPosition(new THREE.Vector3()).z
          ),
          rotation: new Rotation(rot.x, rot.y, rot.z),
          height: wd.userData.entity.height,
          length: wd.userData.entity.length
        });
      });
    }
    return result;
  }

  private _applyOffsetToEdge(offset, number, edgeOrder, floor): Promise<any> {
    const contactEdgePoints = this._getContactEdgesPoints(floor.userData.edges);
    const firstPoint = new THREE.Vector3(
      contactEdgePoints.firstEdge.fp.x,
      0,
      contactEdgePoints.firstEdge.fp.y
    );
    const secondPoint = new THREE.Vector3(
      contactEdgePoints.secondEdge.fp.x,
      0,
      contactEdgePoints.secondEdge.fp.y
    );
    const poisntForDirectionFp = new THREE.Vector3(
      contactEdgePoints.firstEdge.sp.x,
      0,
      contactEdgePoints.firstEdge.sp.y
    );
    const pointForDirectionSp = new THREE.Vector3(
      contactEdgePoints.secondEdge.sp.x,
      0,
      contactEdgePoints.secondEdge.sp.y
    );
    const {
      angles: [firstAngle, secondAngle]
    } = this._currentSpriteData;
    const fpAngle = Math.sin(firstAngle);
    const spAngle = Math.sin(secondAngle);
    let fpOffset = edgeOrder === 'second' ? (offset / spAngle) * fpAngle : offset;
    let spOffset = edgeOrder === 'second' ? offset : (offset / fpAngle) * spAngle;
    const points = floor.userData.points;
    let directionSp, directionFp;
    if (offset >= 0) {
      fpOffset = fpOffset >= 0 ? fpOffset : -1 * fpOffset;
      spOffset = spOffset >= 0 ? spOffset : -1 * spOffset;
      directionFp = new THREE.Vector3(
        firstPoint.x - poisntForDirectionFp.x,
        0,
        firstPoint.z - poisntForDirectionFp.z
      );
      directionSp = new THREE.Vector3(
        secondPoint.x - pointForDirectionSp.x,
        0,
        secondPoint.z - pointForDirectionSp.z
      );
      directionFp.setLength(fpOffset);
      directionSp.setLength(spOffset);
    } else {
      fpOffset = fpOffset < 0 ? fpOffset : -1 * fpOffset;
      spOffset = spOffset < 0 ? spOffset : -1 * spOffset;
      directionFp = new THREE.Vector3(
        poisntForDirectionFp.x - firstPoint.x,
        0,
        poisntForDirectionFp.z - firstPoint.z
      );
      directionSp = new THREE.Vector3(
        pointForDirectionSp.x - secondPoint.x,
        0,
        pointForDirectionSp.z - secondPoint.z
      );
      directionFp.setLength(-fpOffset);
      directionSp.setLength(-spOffset);
    }
    firstPoint.add(directionFp);
    secondPoint.add(directionSp);
    points[number].x = secondPoint.x;
    points[number].y = secondPoint.z;
    points[(number + 1) % this._points.length].x = firstPoint.x;
    points[(number + 1) % this._points.length].y = firstPoint.z;
    return this.floorBuilder.buildFloorAsync({ points }, floor.id);
  }

  private _getContactEdgesPoints(edges) {
    const firstEdge = {
      fp: null,
      sp: null
    };
    const secondEdge = {
      fp: null,
      sp: null
    };
    const edgeIndex = this._currentSpriteData.number;
    const contactEdgesIndexes = this.getContactPointsIndex(edgeIndex, edges.length);
    firstEdge.fp = Object.assign({}, edges[contactEdgesIndexes.first].firstPoint);
    firstEdge.sp = Object.assign({}, edges[contactEdgesIndexes.first].secondPoint);
    secondEdge.fp = Object.assign({}, edges[contactEdgesIndexes.second].secondPoint);
    secondEdge.sp = Object.assign({}, edges[contactEdgesIndexes.second].firstPoint);
    if (!edgeIndex) {
      secondEdge.fp = Object.assign({}, edges[contactEdgesIndexes.first].secondPoint);
      secondEdge.sp = Object.assign({}, edges[contactEdgesIndexes.first].firstPoint);
      firstEdge.fp = Object.assign({}, edges[contactEdgesIndexes.second].firstPoint);
      firstEdge.sp = Object.assign({}, edges[contactEdgesIndexes.second].secondPoint);
    }
    return {
      firstEdge: firstEdge,
      secondEdge: secondEdge
    };
  }

  removeSprites(skipResizeCameraLockSync?: boolean) {
    this._sprites.forEach(s => this.stage.remove(s));
    this._sprites.length = 0;
    if (this._floorHull) {
      this.stage.remove(this._floorHull);
      this._floorHull = null;
    }
    if (!skipResizeCameraLockSync) {
      this._syncMobile3dFloorResizeCameraLock();
    }
  }

  mouseDown(e: MouseEvent) {
    const pe = e as any;
    if (isSecondaryTouchPointer(e)) {
      return;
    }

    this._oldPosition = this.sceneUtils.screenToWorld(e.offsetX, e.offsetY).setY(0);

    if (this.camera.isIn3D()) {
      const cameraPosition = this.camera.camera.position.clone();
      const ray = this.sceneUtils.getRay(e.offsetX, e.offsetY).ray.direction;
      const alpha = cameraPosition.y / ray.y;
      ray.multiplyScalar(alpha);
      cameraPosition.add(ray);
      this._oldPosition = cameraPosition;
    }

    this.sceneUtils.getRay(e.offsetX, e.offsetY, this._raycaster);

    const intersection = this._raycaster.intersectObjects(this._sprites);

    if (intersection.length) {
      e.stopPropagation();
      const sprite = intersection[0].object;
      const data = sprite.userData.data as ISpriteData;
      this._currentSpriteData = data;
      this.moveCamera.disable();
      this._setWallDirection([data.firstPoint, data.secondPoint]);

      const points = this._currentFloor.userData.points;
      this._points = points;
      for (let i = 0; i < points.length; i++) {
        if (points[i].superstructureType === 'wall') {
          points[i].windowsDoors = this._gatherWindowsDoorsData(
            this.select3dService.getSelectedTier(),
            points[i].superstructureId
          );
          points[i].awnings = this.awningSaveDataService.gatherAwningsData(
            this.select3dService.getSelectedTier(),
            points[i].superstructureId
          );
        }
      }
      for (let i = 0; i < points.length; i++) {
        if (i !== data.number && i !== (data.number + 1) % points.length) {
          const point = new THREE.Vector3(points[i].x, 0, points[i].y);
          this._pointsExeptDragged.push(point);
        }
      }

      this._resizeTierAction = new Actions.ResizeTierAction(
        points.map(pnt => ({ ...pnt })),
        this._currentFloor.userData.customId
      );

      this._resizeEdgePointerId = typeof pe.pointerId === 'number' ? pe.pointerId : null;
      this._dragging = true;
    } else {
      this._resizeEdgePointerId = null;
      this.removeSprites();
    }
  }

  mouseMove(e: MouseEvent) {
    if (this._currentSpriteData && this._dragging) {
      e.stopPropagation();

      this._newPosition = this.sceneUtils.screenToWorld(e.offsetX, e.offsetY).setY(0);
      if (this.camera.isIn3D()) {
        const cameraPosition = this.camera.camera.position.clone();
        const ray = this.sceneUtils.getRay(e.offsetX, e.offsetY).ray.direction;
        const alpha = cameraPosition.y / ray.y;
        ray.multiplyScalar(alpha);
        cameraPosition.add(ray);
        this._newPosition = cameraPosition;
        this._newPosition.y = 0;
      }

      const { number: edgeIndex, vectors, angles, normal } = this._currentSpriteData;
      const pointsCount = this._points.length;

      var move = new THREE.Vector3(
        this._newPosition.x - this._oldPosition.x,
        0,
        this._newPosition.z - this._oldPosition.z
      );
      var norm = normal.clone().negate();
      move.projectOnVector(norm);

      if (move.x * vectors[0].x + move.z * vectors[0].z < 0) {
        vectors[0].negate();
        var x = true;
      }

      if (move.x * vectors[1].x + move.z * vectors[1].z < 0) {
        vectors[1].negate();
        var y = true;
      }
      if (this.camera.isIn3D()) {
        vectors[0].negate();

        var x = true;
        vectors[1].negate();
        var y = true;
      }

      if (move.length() !== 0) {
        vectors[0].setLength(move.length() / Math.abs(Math.sin(angles[0])));
        vectors[1].setLength(move.length() / Math.abs(Math.sin(angles[1])));

        var pointA = new THREE.Vector3(this._points[edgeIndex].x, 0, this._points[edgeIndex].y);
        var pointB = new THREE.Vector3(
          this._points[(edgeIndex + 1) % pointsCount].x,
          0,
          this._points[(edgeIndex + 1) % pointsCount].y
        );

        // если у тира есть дочерние либо родительские тиры на сторонах AC или BD, то
        // точки C и D нужно брать как близжайшую из своего эджа и эджа тира
        // чтобы тиры не отрывались друг от друга
        let pointCConfig =
          edgeIndex > 0 ? this._points[edgeIndex - 1] : this._points[pointsCount - 1];
        let pointDConfig = this._points[(edgeIndex + 2) % pointsCount];
        let pointBConfig = this._points[(edgeIndex + 1) % pointsCount];

        var ownPointC = new THREE.Vector3(pointCConfig.x, 0, pointCConfig.y);
        var ownPointD = new THREE.Vector3(pointDConfig.x, 0, pointDConfig.y);
        let pointC = ownPointC,
          pointD = ownPointD;

        var AC = new THREE.Vector3(pointC.x - pointA.x, 0, pointC.z - pointA.z);
        var BD = new THREE.Vector3(pointD.x - pointB.x, 0, pointD.z - pointB.z);
        var codirected1 = AC.x * vectors[0].x + AC.z * vectors[0].z > 0;
        var codirected2 = BD.x * vectors[1].x + BD.z * vectors[1].z > 0;

        let neighbourTiers = [];

        if (
          pointCConfig.superstructureType === 'tier1StepDown' ||
          pointCConfig.superstructureType === 'tier2StepDown' ||
          pointCConfig.superstructureType === 'tier3StepDown' ||
          pointCConfig.superstructureType === 'tier1StepUp' ||
          pointCConfig.superstructureType === 'tier2StepUp' ||
          pointCConfig.superstructureType === 'tier3StepUp'
        ) {
          // this tier is docherniy
          if (pointCConfig.parent) {
            let currentFloor = this._currentFloor;
            let parentTier = this.stage
              .getCompoundFloor()
              .children.find(
                tier => tier.userData.customId === currentFloor.userData.parentCustomId
              );
            neighbourTiers.push(parentTier);
            let parentFirstPointIndex = parentTier.userData.points.findIndex(
              point => point.childCustomId === currentFloor.userData.customId
            );
            let parentPointC =
              parentTier.userData.points[(parentFirstPointIndex + 1) % pointsCount];

            let pC = new THREE.Vector3(parentPointC.x, 0, parentPointC.y);
            let AC = new THREE.Vector3(pointC.x - pointA.x, 0, pointC.z - pointA.z);
            let ApC = new THREE.Vector3(pC.x - pointA.x, 0, pC.z - pointA.z);

            if (
              codirected1 &&
              (ApC.x * AC.x + ApC.z * AC.z) / AC.length() <
                this.constants.inermediateStepSize.width + vectors[0].length()
            ) {
              return;
            }
          }
          // this tier is roditelski
          else {
            let childTier = this.stage
              .getCompoundFloor()
              .children.find(tier => tier.userData.customId === pointCConfig.tierId);
            neighbourTiers.push(childTier);
            let childPointC = childTier.userData.points[0];
            let cC = new THREE.Vector3(childPointC.x, 0, childPointC.y);

            let AC = new THREE.Vector3(pointC.x - pointA.x, 0, pointC.z - pointA.z);
            let AcC = new THREE.Vector3(cC.x - pointA.x, 0, cC.z - pointA.z);

            if (
              codirected1 &&
              (AcC.x * AC.x + AcC.z * AC.z) / AC.length() <
                this.constants.inermediateStepSize.width + vectors[0].length()
            ) {
              return;
            }
          }
        }

        if (
          pointBConfig.superstructureType === 'tier1StepDown' ||
          pointBConfig.superstructureType === 'tier2StepDown' ||
          pointBConfig.superstructureType === 'tier3StepDown' ||
          pointBConfig.superstructureType === 'tier1StepUp' ||
          pointBConfig.superstructureType === 'tier2StepUp' ||
          pointBConfig.superstructureType === 'tier3StepUp'
        ) {
          // this tier is docherniy
          if (pointBConfig.parent) {
            let currentFloor = this._currentFloor;
            let parentTier = this.stage
              .getCompoundFloor()
              .children.find(
                tier => tier.userData.customId === currentFloor.userData.parentCustomId
              );
            neighbourTiers.push(parentTier);
            let parentFirstPointIndex = parentTier.userData.points.findIndex(
              point => point.childCustomId === currentFloor.userData.customId
            );
            let parentPointD = parentTier.userData.points[parentFirstPointIndex];

            let pD = new THREE.Vector3(parentPointD.x, 0, parentPointD.y);
            let BD = new THREE.Vector3(pointD.x - pointB.x, 0, pointD.z - pointB.z);
            let BpD = new THREE.Vector3(pD.x - pointB.x, 0, pD.z - pointB.z);

            if (
              codirected2 &&
              (BpD.x * BD.x + BpD.z * BD.z) / BD.length() <
                this.constants.inermediateStepSize.width + vectors[1].length()
            ) {
              return;
            }
          }
          // this tier is roditelski
          else {
            let childTier = this.stage
              .getCompoundFloor()
              .children.find(tier => tier.userData.customId === pointBConfig.tierId);
            neighbourTiers.push(childTier);
            let childPointD = childTier.userData.points[childTier.userData.points.length - 1];

            let cD = new THREE.Vector3(childPointD.x, 0, childPointD.y);
            let BD = new THREE.Vector3(pointD.x - pointB.x, 0, pointD.z - pointB.z);
            let DcD = new THREE.Vector3(cD.x - pointB.x, 0, cD.z - pointB.z);

            if (
              codirected2 &&
              (DcD.x * BD.x + DcD.z * BD.z) / BD.length() <
                this.constants.inermediateStepSize.width + vectors[1].length()
            ) {
              return;
            }
          }
        }

        pointA.add(vectors[0]);
        pointB.add(vectors[1]);

        const points = this._currentFloor.userData.points;
        // minimal edge length
        if (
          !(
            (codirected1 &&
              AC.length() - this.constants.inermediateStepSize.width < vectors[0].length()) ||
            (codirected2 &&
              BD.length() - this.constants.inermediateStepSize.width < vectors[1].length())
          )
        ) {
          if (this._checkWallDirection(pointA, pointB)) {
            points[edgeIndex].x = pointA.x;
            points[edgeIndex].y = pointA.z;
            const nextEdgePointIndex = (edgeIndex + 1) % pointsCount;
            points[nextEdgePointIndex].x = pointB.x;
            points[nextEdgePointIndex].y = pointB.z;
            this._floorHull.movePoint(edgeIndex, new THREE.Vector3(pointA.x, 0, pointA.z));
            this._floorHull.movePoint(nextEdgePointIndex, new THREE.Vector3(pointB.x, 0, pointB.z));

            this._newPoints = points;
            this._neighbourTiers = neighbourTiers;
            this._updateSprites();

            this.stage.addMany(this.helperLines.getLines());
            this.helperLines.test(pointA, this._pointsExeptDragged);

            this._oldPosition.copy(this._newPosition);

            const count = points.length;
            const edges = [];

            for (let i = 0; i < points.length; i++) {
              const edge = {
                firstPoint: points[i],
                secondPoint: points[(i + 1) % count],
                length: Math.sqrt(
                  Math.pow(points[i].x - points[(i + 1) % count].x, 2) +
                    Math.pow(points[i].y - points[(i + 1) % count].y, 2)
                )
              };
              edges.push(edge);
            }

            this.imageUtils.drawTextSprites(points, edges);
          }
        } else {
          return;
        }
      }
      if (x) {
        vectors[0].negate();
      }
      if (y) {
        vectors[1].negate();
      }

      // this.imageUtils.drawTextSprites();
    }
  }

  mouseUp(e?: any) {
    const pid = e && typeof e.pointerId === 'number' ? e.pointerId : undefined;
    if (
      this._resizeEdgePointerId !== null &&
      pid !== undefined &&
      pid !== this._resizeEdgePointerId
    ) {
      return;
    }

    this._resizeEdgePointerId = null;
    this._dragging = false;
    if (this._currentSpriteData) {
      this.loadingSpinnerService.performAsyncWork(this._applyNewSize.bind(this));
    } else {
      this._currentSpriteData = null;
    }
  }

  getContactPointsIndex(i, length) {
    if (i > 0 && i < length - 1) {
      return {
        first: i + 1,
        second: i - 1
      };
    } else if (i === length - 1) {
      return {
        first: 0,
        second: i - 1
      };
    } else {
      return {
        first: length - 1,
        second: 1
      };
    }
  }

  checkContactTiets(firstPointIndex, secondPointIndex, wallIndex) {
    const floor = this._currentFloor;
    const contactTiers = [];
    if (
      floor.userData.points[firstPointIndex].tierId ||
      floor.userData.points[firstPointIndex].parent
    ) {
      const tierId = floor.userData.points[firstPointIndex].tierId
        ? floor.userData.points[firstPointIndex].tierId
        : floor.userData.parentCustomId;
      let tier = this._getTier(tierId);
      let value = floor.userData.points[firstPointIndex].superstructureType;
      if (wallIndex === 0) {
        contactTiers.push({ pointIndex: 0, edgeOrder: 'second', tier: tier, value: value });
      } else if (wallIndex === floor.userData.points.length - 1) {
        contactTiers.push({
          pointIndex: firstPointIndex,
          edgeOrder: 'first',
          tier: tier,
          value: value
        });
      } else if (wallIndex < firstPointIndex) {
        contactTiers.push({
          pointIndex: firstPointIndex,
          edgeOrder: 'first',
          tier: tier,
          value: value
        });
      } else if (wallIndex > firstPointIndex) {
        firstPointIndex++;
        contactTiers.push({
          pointIndex: firstPointIndex,
          edgeOrder: 'second',
          tier: tier,
          value: value
        });
      }
    }
    if (
      floor.userData.points[secondPointIndex].tierId ||
      floor.userData.points[secondPointIndex].parent
    ) {
      let value = floor.userData.points[secondPointIndex].superstructureType;
      const tierId = floor.userData.points[secondPointIndex].tierId
        ? floor.userData.points[secondPointIndex].tierId
        : floor.userData.parentCustomId;
      let tier = this._getTier(tierId);
      if (wallIndex === 0) {
        contactTiers.push({ pointIndex: 1, edgeOrder: 'first', tier: tier, value: value });
      } else if (wallIndex === floor.userData.points.length - 1) {
        secondPointIndex++;
        contactTiers.push({
          pointIndex: secondPointIndex,
          edgeOrder: 'second',
          tier: tier,
          value: value
        });
      } else if (wallIndex < secondPointIndex) {
        contactTiers.push({
          pointIndex: secondPointIndex,
          edgeOrder: 'first',
          tier: tier,
          value: value
        });
      } else if (wallIndex > secondPointIndex) {
        secondPointIndex++;
        contactTiers.push({
          pointIndex: secondPointIndex,
          edgeOrder: 'second',
          tier: tier,
          value: value
        });
      }
    }
    return contactTiers;
  }

  disable() {
    this._currentFloor = null;
    this.removeSprites();
    this._unSubscribeFromSelect();
    super.disable();
  }

  enable() {
    super.enable();
    this._subscribeForSelect();
  }
}



// WEBPACK FOOTER //
// ./src/app/services/drag-floor-edge.service.ts